<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  rows: { type: Array, required: true },
  money: { type: Function, required: true },
  number: { type: Function, required: true },
});

const { t } = useI18n();
const maxValue = computed(() =>
  Math.max(...props.rows.map(row => Number(row.value)), 1)
);
const filledSegments = value =>
  Math.round((Number(value) / maxValue.value) * 10);
const status = type =>
  ({
    open: {
      label: t('COMMERCIAL.STATUS.OPEN'),
      badge: 'bg-n-blue-3 text-n-blue-11 ring-n-blue-7',
      bar: 'bg-n-blue-9',
    },
    won: {
      label: t('COMMERCIAL.STATUS.WON'),
      badge: 'bg-n-teal-3 text-n-teal-11 ring-n-teal-7',
      bar: 'bg-n-teal-9',
    },
    lost: {
      label: t('COMMERCIAL.STATUS.LOST'),
      badge: 'bg-n-ruby-3 text-n-ruby-11 ring-n-ruby-7',
      bar: 'bg-n-ruby-9',
    },
  })[type];
</script>

<template>
  <section class="min-w-0 break-inside-avoid">
    <div class="mb-3 flex items-end justify-between gap-3">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-n-slate-10">
          {{ t('COMMERCIAL.REPORTS.DETAIL') }}
        </p>
        <h2 class="mt-1 text-lg font-semibold">
          {{ t('COMMERCIAL.REPORTS.PIPELINE') }}
        </h2>
      </div>
      <span class="text-xs text-n-slate-10">
        {{ t('COMMERCIAL.REPORTS.VALUE_SCALE') }}
      </span>
    </div>
    <div
      class="overflow-x-auto rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm print:overflow-visible print:border-slate-300 print:bg-white print:shadow-none"
    >
      <table class="w-full min-w-[680px] text-left text-sm print:min-w-0">
        <thead
          class="border-b border-n-weak bg-n-alpha-2 text-xs text-n-slate-10"
        >
          <tr>
            <th class="px-4 py-3">
              {{ t('COMMERCIAL.REPORTS.COLUMNS.STAGE') }}
            </th>
            <th class="px-4 py-3">
              {{ t('COMMERCIAL.REPORTS.COLUMNS.TYPE') }}
            </th>
            <th class="px-4 py-3 text-end">
              {{ t('COMMERCIAL.REPORTS.COLUMNS.COUNT') }}
            </th>
            <th class="px-4 py-3 text-end">
              {{ t('COMMERCIAL.REPORTS.COLUMNS.VALUE') }}
            </th>
            <th class="w-48 px-4 py-3">
              {{ t('COMMERCIAL.REPORTS.COLUMNS.WEIGHT') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
          >
            <td class="px-4 py-3 font-medium text-n-slate-12">
              {{ row.name }}
            </td>
            <td class="px-4 py-3">
              <span
                class="inline-flex rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset"
                :class="status(row.stage_type).badge"
              >
                {{ status(row.stage_type).label }}
              </span>
            </td>
            <td class="px-4 py-3 text-end tabular-nums">
              {{ number(row.count) }}
            </td>
            <td class="px-4 py-3 text-end font-medium tabular-nums">
              {{ money(row.value) }}
            </td>
            <td class="px-4 py-3">
              <div class="flex gap-1" :aria-label="money(row.value)">
                <span
                  v-for="segment in 10"
                  :key="segment"
                  class="h-2 flex-1 rounded-full"
                  :class="
                    segment <= filledSegments(row.value)
                      ? status(row.stage_type).bar
                      : 'bg-n-slate-4'
                  "
                />
              </div>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="5" class="px-4 py-10 text-center text-n-slate-10">
              {{ t('COMMERCIAL.REPORTS.EMPTY') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
